/**
 * ═══════════════════════════════════════════════════════════════════
 * show-more-sizes.js
 * ═══════════════════════════════════════════════════════════════════
 * Adds a "Show all sizes / Show fewer sizes" toggle button to Variant
 * Swatch King (SKW) size option lists on product pages.
 *
 * WHY THIS EXISTS:
 *   Variant Swatch King renders its own <ul data-preset-id> elements
 *   which replace the native Shopify size buttons. The theme's built-in
 *   show-more logic targets native inputs and doesn't work with SKW.
 *   This script targets SKW's ul[data-preset-id] elements directly.
 *
 * HOW IT WORKS:
 *   1. Finds all SKW size lists (ul[data-preset-id]) that are labelled
 *      with "size" (detected via aria-labelledby → element text).
 *   2. Hides items beyond the configured limit.
 *   3. Injects a full-width toggle button after the list.
 *   4. If the currently selected size is beyond the limit, it silently
 *      expands the visible set to include it.
 *   5. Re-runs on variant change (e.g. colour switch) to reset state.
 *   6. Uses a MutationObserver to handle SKW injecting lists async.
 *
 * CONFIGURATION:
 *   All settings come from data-* attributes on the <variant-picker>
 *   element, which are set by the Shopify theme customiser (Variant
 *   Picker block → Show more sizes section). This means no hardcoded
 *   values need changing here — everything is controlled via the editor.
 *
 *   data-size-limit          — number of sizes to show before "Show more"
 *   data-show-more-desktop   — "true"/"false" — enable on desktop
 *   data-show-more-mobile    — "true"/"false" — enable on mobile
 *   data-btn-bg              — button background colour
 *   data-btn-bg-hover        — button background colour on hover
 *   data-btn-border          — button border colour
 *   data-btn-border-hover    — button border colour on hover
 *   data-btn-text            — button text colour
 *   data-btn-font-size       — button font size (e.g. "0.875em")
 *   data-btn-font-weight     — button font weight (e.g. "500")
 *   data-btn-padding-v       — button vertical padding in px
 *   data-btn-padding-h       — button horizontal padding in px
 *   data-btn-margin-top      — space above the button in px
 *
 * MOBILE BREAKPOINT:
 *   Mobile is defined as window.innerWidth < 768px.
 *
 * UPLOAD TO: assets/show-more-sizes.js
 * LOADED IN: layout/theme.liquid (product pages only, deferred)
 * ═══════════════════════════════════════════════════════════════════
 */

(function () {

  // ─────────────────────────────────────────────────────────────────
  // SETTINGS
  // Read all configuration from data-* attributes on the hidden
  // <variant-picker> element that the Liquid template renders.
  // Falls back to sensible defaults if any attribute is missing.
  // ─────────────────────────────────────────────────────────────────
  function getSettings() {
    var picker = document.querySelector('variant-picker[data-size-limit]');

    // Detect mobile vs desktop to apply the correct enabled toggle
    var isMobile = window.innerWidth < 768;
    var enabledDesktop = picker ? picker.dataset.showMoreDesktop !== 'false' : true;
    var enabledMobile  = picker ? picker.dataset.showMoreMobile  !== 'false' : true;
    var enabled = isMobile ? enabledMobile : enabledDesktop;

    return {
      enabled:     enabled,
      limit:       picker ? (parseInt(picker.dataset.sizeLimit, 10) || 12) : 12,
      bgColor:     picker ? (picker.dataset.btnBg            || '#f0f0f0') : '#f0f0f0',
      bgHover:     picker ? (picker.dataset.btnBgHover        || '#e0e0e0') : '#e0e0e0',
      borderColor: picker ? (picker.dataset.btnBorder         || '#d0d0d0') : '#d0d0d0',
      borderHover: picker ? (picker.dataset.btnBorderHover    || '#b0b0b0') : '#b0b0b0',
      textColor:   picker ? (picker.dataset.btnText           || 'inherit') : 'inherit',
      fontSize:    picker ? (picker.dataset.btnFontSize       || '0.875em') : '0.875em',
      fontWeight:  picker ? (picker.dataset.btnFontWeight     || '500')     : '500',
      paddingV:    picker ? (picker.dataset.btnPaddingV       || '8')       : '8',
      paddingH:    picker ? (picker.dataset.btnPaddingH       || '16')      : '16',
      marginTop:   picker ? (picker.dataset.btnMarginTop      || '8')       : '8',
    };
  }


  // ─────────────────────────────────────────────────────────────────
  // SIZE OPTION DETECTION
  // SKW renders a ul[data-preset-id] for every option (colour, size,
  // etc.). We only want to apply the show-more to SIZE options.
  // Detection: check the element referenced by aria-labelledby and
  // see if its text contains "size" (case-insensitive).
  // ─────────────────────────────────────────────────────────────────
  function isSizeOption(ul) {
    var labelledBy = ul.getAttribute('aria-labelledby');
    if (!labelledBy) return false;
    var legend = document.getElementById(labelledBy);
    if (!legend) return false;
    return legend.textContent.toLowerCase().indexOf('size') !== -1;
  }


  // ─────────────────────────────────────────────────────────────────
  // APPLY SHOW MORE
  // Main function — called for each SKW size list found on the page.
  // Guards against double-initialisation via dataset.showMoreInit.
  // ─────────────────────────────────────────────────────────────────
  function applyShowMore(ul) {
    // Guard: already initialised on this element
    if (ul.dataset.showMoreInit) return;

    var s = getSettings();

    // Guard: feature disabled for current device type
    if (!s.enabled) return;

    var items = Array.from(ul.querySelectorAll('li.swatch-view-item'));

    // Guard: not enough items to need truncation
    if (items.length <= s.limit) return;

    ul.dataset.showMoreInit = 'true';

    // ── Selected size detection ──────────────────────────────────
    // If the currently selected size falls beyond the visible limit,
    // silently extend the limit to include it so the user always
    // sees their current selection without having to click "Show more".
    var selectedIndex = -1;
    items.forEach(function (item, i) {
      if (item.getAttribute('aria-checked') === 'true') selectedIndex = i;
    });

    var effectiveLimit = s.limit;
    if (selectedIndex >= s.limit) effectiveLimit = selectedIndex + 1;

    // ── Hide items beyond the effective limit ─────────────────────
    items.forEach(function (item, i) {
      if (i >= effectiveLimit) item.style.display = 'none';
    });

    var hiddenCount = items.length - effectiveLimit;
    if (hiddenCount <= 0) return;

    // ── Create the toggle button ──────────────────────────────────
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'size-show-more-swk';
    btn.setAttribute('aria-expanded', 'false');

    // Full-width, square corners, styled to match the size buttons
    btn.style.cssText = [
      'display:flex',
      'align-items:center',
      'justify-content:center',
      'width:100%',
      'cursor:pointer',
      'background-color:' + s.bgColor,
      'border:1px solid ' + s.borderColor,
      'border-radius:0',
      'padding:' + s.paddingV + 'px ' + s.paddingH + 'px',
      'font-size:' + s.fontSize,
      'font-weight:' + s.fontWeight,
      'color:' + s.textColor,
      'text-decoration:none',
      'margin-top:' + s.marginTop + 'px',
      'transition:background-color 0.15s ease, border-color 0.15s ease',
      'letter-spacing:0.01em',
      'box-sizing:border-box'
    ].join(';');

    // Hover state — darken background and border slightly
    btn.addEventListener('mouseenter', function () {
      this.style.backgroundColor = s.bgHover;
      this.style.borderColor = s.borderHover;
    });
    btn.addEventListener('mouseleave', function () {
      this.style.backgroundColor = s.bgColor;
      this.style.borderColor = s.borderColor;
    });

    // ── Button label ──────────────────────────────────────────────
    // Shows "+N more" when collapsed, or "Show fewer" when expanded.
    function updateText(expanded, currentHidden) {
      btn.textContent = expanded
        ? 'Show fewer sizes'
        : 'Show all sizes (+' + currentHidden + ' more)';
    }

    updateText(false, hiddenCount);
    ul.parentNode.insertBefore(btn, ul.nextSibling);

    // ── Toggle click handler ──────────────────────────────────────
    btn.addEventListener('click', function () {
      var expanded = this.getAttribute('aria-expanded') === 'true';

      // Re-check selected index at click time (variant may have changed)
      var currentSelected = -1;
      items.forEach(function (item, i) {
        if (item.getAttribute('aria-checked') === 'true') currentSelected = i;
      });

      var stillHidden = 0;
      items.forEach(function (item, i) {
        if (i >= effectiveLimit) {
          if (expanded) {
            // Collapsing: hide all except currently selected
            if (i !== currentSelected) {
              item.style.display = 'none';
              stillHidden++;
            }
          } else {
            // Expanding: show all
            item.style.display = '';
          }
        }
      });

      this.setAttribute('aria-expanded', String(!expanded));
      updateText(!expanded, stillHidden);
    });
  }


  // ─────────────────────────────────────────────────────────────────
  // INIT
  // Find all SKW size lists currently in the DOM and apply show-more.
  // Called on DOMContentLoaded and after variant changes.
  // ─────────────────────────────────────────────────────────────────
  function init() {
    document.querySelectorAll('ul[data-preset-id]').forEach(function (ul) {
      if (isSizeOption(ul)) applyShowMore(ul);
    });
  }


  // ─────────────────────────────────────────────────────────────────
  // MUTATION OBSERVER
  // SKW injects its ULs asynchronously after page load. Watch the DOM
  // for new ul[data-preset-id] elements and apply show-more to them.
  // ─────────────────────────────────────────────────────────────────
  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return; // Element nodes only
        var uls = node.matches && node.matches('ul[data-preset-id]')
          ? [node]
          : Array.from((node.querySelectorAll && node.querySelectorAll('ul[data-preset-id]')) || []);
        uls.forEach(function (ul) {
          if (isSizeOption(ul)) applyShowMore(ul);
        });
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });


  // ─────────────────────────────────────────────────────────────────
  // DOM READY
  // Run init immediately if DOM is ready, otherwise wait.
  // ─────────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }


  // ─────────────────────────────────────────────────────────────────
  // VARIANT CHANGE
  // When the user switches colour (or any other option), SKW re-renders
  // its size list. We tear down the existing show-more state and
  // re-initialise 300ms later to give SKW time to finish rendering.
  // ─────────────────────────────────────────────────────────────────
  document.addEventListener('on:variant:change', function () {
    setTimeout(function () {
      document.querySelectorAll('ul[data-preset-id]').forEach(function (ul) {
        // Reset init flag so applyShowMore will run again
        delete ul.dataset.showMoreInit;
        // Remove the old toggle button if present
        var next = ul.nextSibling;
        if (next && next.classList && next.classList.contains('size-show-more-swk')) {
          next.parentNode.removeChild(next);
        }
      });
      init();
    }, 300);
  });

})();